import React, { useCallback, useMemo, useState } from "react";
import { ITableOfTableItem } from "../../../../../../modules/API/Defaults/models/tableOfTables";
import { Drivers } from "../../../../../../modules/API/Drivers/drivers";
import {
  ICustomerItem,
  IKavDataItem,
  IOrderItem,
} from "../../../../../../modules/API/Drivers/models/kavData";
import {
  applyOrdersConfirm,
  toggleOrderSelection,
} from "../../helpers/driversState";

export interface IUseDeliveryConfirm {
  selectedDeliveryOrder: IOrderItem[] | undefined;
  selectedKav: number;
  isOpenDriverModal: boolean;
  handleControlFuncModal: () => void;
  selectOrderForCloseDelivery: (
    item: IOrderItem[],
    dataCustomer: ICustomerItem,
    kavID: number
  ) => void;
  handleSelectOrder: (item: IOrderItem) => void;
  saveToLogOrder: (action: number, areaID: number) => Promise<void>;
  descForDriverModal: string;
  driverModalProps: object;
}

export const useDeliveryConfirm = (deps: {
  setData: React.Dispatch<React.SetStateAction<IKavDataItem[] | null>>;
  date: string;
  customerData: ICustomerItem | undefined;
  setCustomerData: (customer: ICustomerItem) => void;
  defaultStatuses: ITableOfTableItem[] | undefined;
  listTeudotActions: ITableOfTableItem[] | undefined;
}): IUseDeliveryConfirm => {
  const {
    setData,
    date,
    customerData,
    setCustomerData,
    defaultStatuses,
    listTeudotActions,
  } = deps;

  const [selectedDeliveryOrder, setSelectedDeliveryOrder] =
    useState<IOrderItem[]>();
  const [selectedKav, setSelectedKav] = useState<number>(0);
  const [isOpenDriverModal, setIsOpenDriverModal] = useState(false);

  const handleControlFuncModal = useCallback(() => {
    setIsOpenDriverModal((prev) => !prev);
  }, []);

  const selectOrderForCloseDelivery = useCallback(
    (item: IOrderItem[], dataCustomer: ICustomerItem, kavID: number) => {
      // В модалку попадают только теудот с номером — легаси-фильтр.
      setCustomerData(dataCustomer);
      setSelectedKav(kavID);
      setSelectedDeliveryOrder(item.filter((e) => e.Delivery_Num !== 0));
      setIsOpenDriverModal(true);
    },
    [setCustomerData]
  );

  const handleSelectOrder = useCallback((item: IOrderItem) => {
    if (item.Delivery_Num === 0) return;
    setSelectedDeliveryOrder((prev) =>
      prev ? toggleOrderSelection(prev, item.OrderID) : prev
    );
  }, []);

  const saveToLogOrder = useCallback(
    // areaID остаётся в сигнатуре ради контракта модалки; легаси брал кав из
    // selectedKav, зафиксированного при открытии.
    async (action: number, areaID: number) => {
      if (!selectedDeliveryOrder) return;
      const postData = selectedDeliveryOrder.map((e) => ({
        Action: action,
        ActionTblCode: 128,
        Delivery_Date: date,
        PersonID: customerData?.CustomerID || 0,
        PersonType: customerData?.Type || 0,
        ReferenceId: e.Delivery_Num,
        ReferenceType: 1,
        ReferenceSubId: e.Delivery_Sub,
        TransferZone: selectedKav || 0,
      }));
      try {
        await Drivers.logStatusDelivery(postData);
        setIsOpenDriverModal(false);
        const description =
          listTeudotActions?.find((e) => e.SecondCode === action)?.Description ||
          "";
        setData((prev) =>
          prev
            ? applyOrdersConfirm(prev, selectedDeliveryOrder, action, description)
            : prev
        );
        setSelectedDeliveryOrder((prev) =>
          prev ? prev.map((e) => ({ ...e, Selected: false })) : prev
        );
      } catch {
        // легаси молча проглатывал ошибку
      }
    },
    [
      selectedDeliveryOrder,
      date,
      customerData,
      selectedKav,
      listTeudotActions,
      setData,
    ]
  );

  const descForDriverModal =
    selectedDeliveryOrder && selectedDeliveryOrder[0]?.Order_Type === 1
      ? "אישור מסירה"
      : "אישור איסוף";

  const driverModalProps = useMemo(
    () => ({
      confirmDeliveryOrder: saveToLogOrder,
      selectedDeliveryOrder,
      defaultStatuses,
      handleSelectOrder,
      actionList: listTeudotActions,
    }),
    [
      saveToLogOrder,
      selectedDeliveryOrder,
      defaultStatuses,
      handleSelectOrder,
      listTeudotActions,
    ]
  );

  return {
    selectedDeliveryOrder,
    selectedKav,
    isOpenDriverModal,
    handleControlFuncModal,
    selectOrderForCloseDelivery,
    handleSelectOrder,
    saveToLogOrder,
    descForDriverModal,
    driverModalProps,
  };
};

export default useDeliveryConfirm;
